import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useDebounce } from "@/hooks/useDebounce";
import { DataSiswa, PaginationData } from "@/types";
import { Search, Settings2 } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Pagination } from "./Pagination";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  paginationData: PaginationData<DataSiswa>;
  isLoading?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  paginationData,
  isLoading = false,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );
  const debouncedQuery = useDebounce(searchQuery, 1000);

  const navigate = useNavigate();
  const location = useLocation();

  const statusValidasiParams = searchParams.get("statusValidasi") || "semua";

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  // Handle Search Debounce
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (debouncedQuery) {
      // if (params.get("search") !== debouncedQuery) {
      //   params.delete("page");
      // }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search");
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search]);

  // Handle Tab Change
  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === "semua") {
      newParams.delete("statusValidasi");
    } else {
      newParams.set("statusValidasi", value);
    }
    newParams.set("page", "1"); // Reset ke halaman 1 tiap ganti tab
    setSearchParams(newParams);
  };

  return (
    <div className="flex flex-col w-full">
      {/* --- TOOLBAR: SEARCH & FILTER --- */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 p-4 lg:p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
        {/* Search Input */}
        <div className="relative w-full lg:max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-slate-400" />
          </div>
          <Input
            placeholder="Cari NIK, NISN, atau Nama Peserta..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="pl-10 bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 w-full focus-visible:ring-primary h-10"
          />
        </div>

        {/* Tabs & Columns Toggle */}
        <div className="flex items-center gap-3 w-full lg:w-auto overflow-x-auto pb-1 lg:pb-0">
          <Tabs
            value={statusValidasiParams}
            onValueChange={handleTabChange}
            className="w-full sm:w-auto shrink-0"
          >
            <TabsList className="grid w-full grid-cols-3 rounded-full bg-slate-200/60 dark:bg-slate-800 p-1 h-10">
              <TabsTrigger
                value="semua"
                className="rounded-full text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700"
              >
                Semua
              </TabsTrigger>
              <TabsTrigger
                value="belum-valid"
                className="rounded-full text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700"
              >
                Belum Diverifikasi
              </TabsTrigger>
              <TabsTrigger
                value="valid"
                className="rounded-full text-sm data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700"
              >
                Sudah Diverifikasi
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="h-10 shrink-0 bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700"
              >
                <Settings2 className="h-4 w-4 mr-2 text-slate-500" />
                Kolom
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id.replace(/_/g, " ")}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* --- TABLE AREA --- */}
      <div className="w-full overflow-x-auto min-h-[400px]">
        <Table className="w-full">
          <TableHeader className="bg-slate-50 dark:bg-slate-900/80 sticky top-0 z-10 shadow-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="border-b border-slate-200 dark:border-slate-800 hover:bg-transparent"
              >
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className="h-12 px-4 text-slate-600 dark:text-slate-300 font-semibold whitespace-nowrap"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // State Loading: Tampilkan Skeleton Row
              Array.from({ length: 5 }).map((_, rowIndex) => (
                <TableRow
                  key={`skeleton-row-${rowIndex}`}
                  className="border-b border-slate-100 dark:border-slate-800/60"
                >
                  {columns.map((_, colIndex) => (
                    <TableCell
                      key={`skeleton-col-${colIndex}`}
                      className="py-4 px-4"
                    >
                      <div className="h-5 bg-slate-200 dark:bg-slate-800 rounded-md w-full animate-pulse"></div>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              // State Sukses: Render Data Baris
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-3 px-4">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              // State Kosong: Tidak Ada Data
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-48 text-center text-slate-500 dark:text-slate-400"
                >
                  <div className="flex flex-col items-center justify-center">
                    <Search className="h-8 w-8 mb-3 opacity-20" />
                    <p>Peserta tidak ditemukan atau belum ada data.</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* --- FOOTER PAGINATION --- */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
        <Pagination table={table} paginationData={paginationData} />
      </div>
    </div>
  );
}
